/**
 * Launch Activation test-id registry — single source of truth for every
 * `data-testid` this module renders. Mirrored VERBATIM in
 * @test-kit/selectors/launch-activation.ts (.claude + .agents) — keep in sync.
 */
export const LAUNCH_ACTIVATION_TEST_IDS = {
  page: 'launch-activation-page',
  list: 'launch-activation-list',
  item: (id: string) => `launch-activation-item-${id}`,
  itemAction: (id: string) => `launch-activation-item-${id}-action`,
  // Additive (Plan B, Task 7, AC-056): retry affordance for a row whose
  // evaluator threw (`ActivationItem.checkFailed`); never counted as earned.
  itemRetry: (id: string) => `launch-activation-item-retry-${id}`,
  progress: 'launch-activation-progress',
  next: 'launch-activation-next',
  drawer: 'launch-activation-drawer',
  drawerVerify: 'launch-activation-drawer-verify',
  drawerClose: 'launch-activation-drawer-close',
  badge: 'launch-activation-badge',
  popover: 'launch-activation-popover',
  shortForm: 'launch-activation-short-form',
  /** localStorage key; value 'on' brings the hidden footer short form back (sidebar plan D-10/D-27). */
  shortFormFlagStorageKey: 'sidebar-activation-short-form',
  shortFormNext: 'launch-activation-short-form-next',
  shortFormAction: 'launch-activation-short-form-action',
  shortFormAll: 'launch-activation-short-form-all',
  chip: 'launch-activation-chip',
  advisory: (id: string) => `launch-activation-advisory-${id}`,
  intentLink: 'launch-activation-intent-link',
  // Additive (Plan B, Task 8): full-variant chrome (AM-4/AM-5) — header goal
  // line, domain gate panel, complete-state collapse, top-level error retry.
  intentGoal: 'launch-activation-intent-goal',
  intentChange: 'launch-activation-intent-change',
  domainGate: 'launch-activation-domain-gate',
  domainGateAction: 'launch-activation-domain-gate-action',
  complete: 'launch-activation-complete',
  completeToggle: 'launch-activation-complete-toggle',
  retry: 'launch-activation-retry',

  // ── Additive (Plan D follow-up F2, contracts §10 AM-16): the in-place intent
  // popover opened from the header's `change` link. Editing the whole intent
  // (goal + channels + product analytics) is what re-derives the list, so all
  // three controls live in one Popover with a single primary action.
  intentPopover: 'launch-activation-intent-popover',
  intentGoalSelect: 'launch-activation-intent-goal-select',
  intentChannel: (id: string) => `launch-activation-intent-channel-${id}`,
  intentAnalytics: (id: string) => `launch-activation-intent-analytics-${id}`,
  intentApply: 'launch-activation-intent-apply',
  intentError: 'launch-activation-intent-error',
  intentQuestionnaire: 'launch-activation-intent-questionnaire',

  // ── Tier-2 publish gate (Plan C renders; PRE-012, contracts §10 AM-5) ──
  // Lives here — NOT as a separate `PUBLISH_GATE` — because AM-5 mandates
  // `LAUNCH_ACTIVATION.gate*`; Plan C is written against these exact values.
  gateSummary: 'launch-gate-summary',
  gateFinding: (id: string) => `launch-gate-finding-${id}`,
  gateFindingAction: (id: string, index: number) => `launch-gate-finding-action-${id}-${index}`,
  gateVendorPath: (id: string) => `launch-gate-vendor-path-${id}`,
  gateArticle: (id: string) => `launch-gate-article-${id}`,
  gateUnavailable: 'launch-gate-unavailable',
  gateClean: 'launch-gate-clean',
  gatePublish: 'launch-gate-publish',
  gateFixFirst: 'launch-gate-fix-first',
  // ── Class-X vendor click-path on a checklist item (DC-13) ──
  vendorPath: (id: string) => `launch-activation-vendor-path-${id}`,
} as const;
